import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../components/Form';
import { Chip } from '../components/Chips';
import { EmptyState, ErrorState, InlineError, TableSkeleton } from '../components/Feedback';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { ConfirmDialog, Drawer } from '../components/Overlay';
import { VendorLookupField } from '../components/VendorLookupField';
import { useAuth } from '../lib/auth';
import { partnerConnections, partnerEntitlements } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime, formatRelative } from '../lib/format';
import type { DirectoryEntryDto, PartnerConnectionDto, RelationshipRole } from '../lib/types';

type ConnectionColumn = 'counterparty' | 'relationship' | 'status' | 'qboRecord' | 'exchange';

function connectionSortValue(c: PartnerConnectionDto, key: ConnectionColumn): string | number {
  switch (key) {
    case 'counterparty':
      return c.counterpartyCompanyName ?? '';
    case 'relationship':
      return c.relationshipRole;
    case 'status':
      return c.status;
    case 'qboRecord':
      return c.boundQboVendorId ?? c.boundQboCustomerId ?? '';
    case 'exchange':
      return (c.isBuyer ? c.buyerInvoiceReceivingEnabled : c.sellerInvoiceSendingEnabled) ? 1 : 0;
    default:
      return '';
  }
}

type DirectoryResultColumn = 'company' | 'location' | 'identifiers';

function directoryResultSortValue(entry: DirectoryEntryDto, key: DirectoryResultColumn): string | number {
  switch (key) {
    case 'company':
      return entry.companyName ?? '';
    case 'location':
      return [entry.city, entry.county, entry.countryRegionCode].filter(Boolean).join(', ');
    case 'identifiers':
      return entry.businessEmailDomain ?? '';
    default:
      return '';
  }
}

const ROLE_OPTIONS = [
  { value: 'MyVendor', label: 'They are my vendor — they invoice us' },
  { value: 'MyCustomer', label: 'They are my customer — we invoice them' },
  { value: 'TradingPartner', label: 'Both — we invoice each other' },
];

/** Requested and Accepted are the only states anything can be done from. */
function statusTone(status: string): 'good' | 'warn' | 'neutral' | 'bad' {
  switch (status) {
    case 'Accepted':
      return 'good';
    case 'Requested':
      return 'warn';
    case 'Rejected':
    case 'Revoked':
      return 'bad';
    default:
      return 'neutral';
  }
}

/**
 * TRADING-PARTNER CONNECTIONS — finding a company, agreeing to exchange with it, and saying which
 * QuickBooks record it is.
 *
 * THE CENTRAL STORE IS THE AUTHORITY, NOT THIS SCREEN. Every connection here is a mirror of a row
 * iERP AI owns, which is why the list refreshes from central before it renders and why the last
 * sync time is on every row. A stale mirror is the normal cost of a distributed agreement, and
 * hiding it would leave an operator arguing with a screen that is quietly out of date.
 *
 * THE BINDING IS THE PART THAT TOUCHES ACCOUNTING. An accepted connection means two companies have
 * agreed to exchange documents; the binding says WHICH QuickBooks Vendor or Customer the
 * counterparty actually is. Until it is bound, an inbound invoice has no supplier to post against -
 * so binding is presented as the step that finishes the connection rather than an optional detail.
 *
 * Which side may set which capability is not this screen's opinion: only the seller controls
 * invoice sending and only the buyer controls receiving. The server refuses the wrong side with
 * "WrongSide", and this screen shows only the switch that belongs to this company.
 */
export function PartnerConnectionsPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();

  const canSearch = can('partners.search');
  const canManage = can('partners.manage');

  const list = useAsync((signal) => partnerConnections.list(signal), []);
  const entitlement = useAsync((signal) => partnerEntitlements.get(signal), []);

  const connectionsTable = useSortableTable<ConnectionColumn>({
    storageKey: 'partnerConnectionsColumnWidths',
    columns: {
      counterparty: { minWidth: 160 },
      relationship: { minWidth: 140 },
      status: { minWidth: 120 },
      qboRecord: { minWidth: 160 },
      exchange: { minWidth: 130 },
    },
  });
  const directoryResultsTable = useSortableTable<DirectoryResultColumn>({
    storageKey: 'partnerDirectoryResultsColumnWidths',
    columns: {
      company: { minWidth: 180 },
      location: { minWidth: 160 },
      identifiers: { minWidth: 160 },
    },
  });

  // A vendor card can deep-link here with ?vendorId=&vendorName= to start a handshake meant for a
  // specific QuickBooks vendor - the directory search is prefilled with the vendor's name, and
  // Request Connection below carries the vendor straight into the binding instead of starting blank.
  const [searchParams] = useSearchParams();
  const prefillVendorId = searchParams.get('vendorId');
  const prefillVendorName = searchParams.get('vendorName');

  const [searchText, setSearchText] = useState(() => prefillVendorName ?? '');
  const [country, setCountry] = useState('');
  const [postCode, setPostCode] = useState('');
  const [results, setResults] = useState<DirectoryEntryDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchNote, setSearchNote] = useState<string | null>(null);

  const [requesting, setRequesting] = useState<DirectoryEntryDto | null>(null);
  const [role, setRole] = useState<RelationshipRole>('MyVendor');
  const [requestMessage, setRequestMessage] = useState('');
  const [localVendorId, setLocalVendorId] = useState('');
  const [localVendorName, setLocalVendorName] = useState<string | null>(null);
  const [localCustomerId, setLocalCustomerId] = useState('');

  const [managing, setManaging] = useState<PartnerConnectionDto | null>(null);
  const [bindVendorId, setBindVendorId] = useState('');
  const [bindVendorName, setBindVendorName] = useState<string | null>(null);
  const [bindCustomerId, setBindCustomerId] = useState('');

  const [currencyLinkCode, setCurrencyLinkCode] = useState('');
  const [currencyLinkVendorId, setCurrencyLinkVendorId] = useState('');
  const [currencyLinkVendorName, setCurrencyLinkVendorName] = useState<string | null>(null);

  const [revoking, setRevoking] = useState<PartnerConnectionDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      toast.success(label);
      await list.reload();
    } catch (caught) {
      const message = errorMessage(caught);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const search = async () => {
    setSearching(true);
    setSearchNote(null);
    try {
      const response = await partnerConnections.search({
        searchText: searchText || null,
        countryRegionCode: country || null,
        postCode: postCode || null,
        take: 25,
      });
      setResults(response.companies);
      setSearchNote(
        response.companies.length === 0
          ? response.message ?? 'No companies matched.'
          : `${response.companies.length} of ${response.totalCount} shown.`,
      );
    } catch (caught) {
      toast.error(errorMessage(caught));
      setSearchNote(errorMessage(caught));
    } finally {
      setSearching(false);
    }
  };

  // Fires once, only when arriving from a vendor card's "Search the directory" link - an ordinary
  // visit to this page starts with an empty search, same as always.
  useEffect(() => {
    if (prefillVendorName) void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openManage = (connection: PartnerConnectionDto) => {
    setManaging(connection);
    setBindVendorId(connection.boundQboVendorId ?? '');
    // The connection mirror only carries the bound id, not its display name - VendorLookupField
    // falls back to a generic "Selected vendor" label until someone changes the selection.
    setBindVendorName(null);
    setBindCustomerId(connection.boundQboCustomerId ?? '');
    setCurrencyLinkCode('');
    setCurrencyLinkVendorId('');
    setCurrencyLinkVendorName(null);
    setError(null);
  };

  const exchangeBlocked = entitlement.data && !entitlement.data.startHandshake.enabled;

  return (
    <>
      <PageHeader
        title="Trading Partners"
        description="Companies this one has agreed to exchange documents with, and which QuickBooks record each of them is."
        actions={
          canManage ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() =>
                void run('This company profile was sent to every accepted connection.', () =>
                  partnerConnections.broadcastCompanyInfo(),
                )
              }
            >
              Send company info to all partners
            </button>
          ) : undefined
        }
      />

      {exchangeBlocked ? (
        <Panel title="Starting a handshake is not available to this company">
          <p className="muted">
            {entitlement.data?.startHandshake.message
              ?? 'iERP AI did not grant this company permission to start trading-partner handshakes.'}
          </p>
          <p className="muted">
            Existing connections below still work. Checked{' '}
            {entitlement.data ? formatDateTime(entitlement.data.startHandshake.checkedUtc) : '—'}.
          </p>
        </Panel>
      ) : null}

      <InlineError message={error} />

      <Panel
        title="Connections"
        description="Read from the central store each time this page loads, so what is shown is the agreement as iERP AI holds it."
      >
        {list.loading && !list.data ? (
          <TableSkeleton rows={4} columns={6} />
        ) : list.error ? (
          <ErrorState
            message="Connections could not be read from iERP AI."
            detail={list.error}
            onRetry={list.reload}
          />
        ) : !list.data || list.data.length === 0 ? (
          <EmptyState
            title="No trading partners yet"
            description={
              canSearch
                ? 'Search the directory below to find a company, then request a connection. They accept it on their side before anything can be exchanged.'
                : 'An operator can search the directory and request a connection.'
            }
          />
        ) : (
          <TableWrap busy={list.loading} tableClassName="data-table-sortable">
            <colgroup>
              <col style={connectionsTable.columnStyle('counterparty')} />
              <col style={connectionsTable.columnStyle('relationship')} />
              <col style={connectionsTable.columnStyle('status')} />
              <col style={connectionsTable.columnStyle('qboRecord')} />
              <col style={connectionsTable.columnStyle('exchange')} />
              <col className="col-actions" />
            </colgroup>
            <thead>
              <tr>
                <SortableTh
                  label="Counterparty"
                  sortKey="counterparty"
                  currentSortKey={connectionsTable.sortKey}
                  sortDir={connectionsTable.sortDir}
                  onSort={connectionsTable.toggleSort}
                  style={connectionsTable.columnStyle('counterparty')}
                  onResizeStart={connectionsTable.beginResize('counterparty')}
                />
                <SortableTh
                  label="Relationship"
                  sortKey="relationship"
                  currentSortKey={connectionsTable.sortKey}
                  sortDir={connectionsTable.sortDir}
                  onSort={connectionsTable.toggleSort}
                  style={connectionsTable.columnStyle('relationship')}
                  onResizeStart={connectionsTable.beginResize('relationship')}
                />
                <SortableTh
                  label="Status"
                  sortKey="status"
                  currentSortKey={connectionsTable.sortKey}
                  sortDir={connectionsTable.sortDir}
                  onSort={connectionsTable.toggleSort}
                  style={connectionsTable.columnStyle('status')}
                  onResizeStart={connectionsTable.beginResize('status')}
                />
                <SortableTh
                  label="QuickBooks record"
                  sortKey="qboRecord"
                  currentSortKey={connectionsTable.sortKey}
                  sortDir={connectionsTable.sortDir}
                  onSort={connectionsTable.toggleSort}
                  style={connectionsTable.columnStyle('qboRecord')}
                  onResizeStart={connectionsTable.beginResize('qboRecord')}
                />
                <SortableTh
                  label="Exchange"
                  sortKey="exchange"
                  currentSortKey={connectionsTable.sortKey}
                  sortDir={connectionsTable.sortDir}
                  onSort={connectionsTable.toggleSort}
                  style={connectionsTable.columnStyle('exchange')}
                  onResizeStart={connectionsTable.beginResize('exchange')}
                />
                <th scope="col" className="col-actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {connectionsTable.sort(list.data, connectionSortValue).map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className="cell-strong">{c.counterpartyCompanyName ?? 'Unnamed company'}</span>
                    <span className="cell-sub">
                      {c.direction === 'Inbound' ? 'they asked' : 'we asked'} ·{' '}
                      {c.requestedUtc ? formatRelative(c.requestedUtc) : '—'}
                    </span>
                  </td>
                  <td>
                    {c.relationshipRole}
                    <span className="cell-sub">{c.isBuyer ? 'we are the buyer' : 'we are the seller'}</span>
                  </td>
                  <td>
                    <Chip tone={statusTone(c.status)}>{c.status}</Chip>
                    {c.hasPendingCompanyInfoUpdate ? (
                      <span className="cell-flag warn">company info to review</span>
                    ) : null}
                  </td>
                  <td>
                    {c.boundQboVendorId ? (
                      <>
                        Vendor <span className="mono">{c.boundQboVendorId}</span>
                      </>
                    ) : c.boundQboCustomerId ? (
                      <>
                        Customer <span className="mono">{c.boundQboCustomerId}</span>
                      </>
                    ) : (
                      <span className="cell-flag warn">not bound</span>
                    )}
                    <span className="cell-sub">
                      {c.boundUtc ? `bound ${formatRelative(c.boundUtc)}` : 'an invoice cannot post until this is set'}
                    </span>
                  </td>
                  <td>
                    {/* Only the switch this company owns. The other side's is not ours to show. */}
                    {c.isBuyer ? (
                      <Chip tone={c.buyerInvoiceReceivingEnabled ? 'good' : 'neutral'}>
                        {c.buyerInvoiceReceivingEnabled ? 'receiving' : 'paused'}
                      </Chip>
                    ) : (
                      <Chip tone={c.sellerInvoiceSendingEnabled ? 'good' : 'neutral'}>
                        {c.sellerInvoiceSendingEnabled ? 'sending' : 'paused'}
                      </Chip>
                    )}
                    <span className="cell-sub">synced {formatRelative(c.lastSyncedUtc)}</span>
                  </td>
                  <td className="col-actions">
                    {canManage && c.status === 'Requested' && c.direction === 'Inbound' ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={busy}
                          onClick={() => openManage(c)}
                        >
                          Answer
                        </button>
                      </>
                    ) : null}
                    {canManage && c.status === 'Accepted' ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => openManage(c)}
                      >
                        Manage
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      {canSearch ? (
        <Panel
          title="Find a company"
          description="Only companies that published themselves to the directory and chose to be listed appear here."
        >
          <Toolbar>
            <TextField
              label="Company name, trade name or domain"
              type="search"
              value={searchText}
              onChange={setSearchText}
              className="toolbar-grow"
            />
            <TextField label="Country code" value={country} onChange={setCountry} placeholder="US" />
            <TextField label="Post code" value={postCode} onChange={setPostCode} />
            <button
              type="button"
              className="btn btn-secondary"
              disabled={searching}
              onClick={() => void search()}
            >
              {searching ? 'Searching…' : 'Search'}
            </button>
          </Toolbar>

          {searchNote ? <p className="muted">{searchNote}</p> : null}

          {results && results.length > 0 ? (
            <TableWrap tableClassName="data-table-sortable">
              <colgroup>
                <col style={directoryResultsTable.columnStyle('company')} />
                <col style={directoryResultsTable.columnStyle('location')} />
                <col style={directoryResultsTable.columnStyle('identifiers')} />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Company"
                    sortKey="company"
                    currentSortKey={directoryResultsTable.sortKey}
                    sortDir={directoryResultsTable.sortDir}
                    onSort={directoryResultsTable.toggleSort}
                    style={directoryResultsTable.columnStyle('company')}
                    onResizeStart={directoryResultsTable.beginResize('company')}
                  />
                  <SortableTh
                    label="Location"
                    sortKey="location"
                    currentSortKey={directoryResultsTable.sortKey}
                    sortDir={directoryResultsTable.sortDir}
                    onSort={directoryResultsTable.toggleSort}
                    style={directoryResultsTable.columnStyle('location')}
                    onResizeStart={directoryResultsTable.beginResize('location')}
                  />
                  <SortableTh
                    label="Identifiers"
                    sortKey="identifiers"
                    currentSortKey={directoryResultsTable.sortKey}
                    sortDir={directoryResultsTable.sortDir}
                    onSort={directoryResultsTable.toggleSort}
                    style={directoryResultsTable.columnStyle('identifiers')}
                    onResizeStart={directoryResultsTable.beginResize('identifiers')}
                  />
                  <th scope="col" className="col-actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {directoryResultsTable.sort(results, directoryResultSortValue).map((entry) => (
                  <tr key={entry.profileId ?? `${entry.companyName}-${entry.postCode}`}>
                    <td>
                      <span className="cell-strong">{entry.companyName ?? 'Unnamed company'}</span>
                      <span className="cell-sub">
                        {entry.tradeName ?? ''}
                        {entry.anonymousDirectoryEntry ? ' · limited disclosure' : ''}
                      </span>
                    </td>
                    <td>
                      {[entry.city, entry.county, entry.countryRegionCode].filter(Boolean).join(', ') || '—'}
                      <span className="cell-sub">{entry.postCode ?? ''}</span>
                    </td>
                    <td>
                      {entry.businessEmailDomain ?? '—'}
                      <span className="cell-sub">
                        {/*
                          A matched tax registration is the strongest signal that this is the
                          company the operator meant, so it is stated rather than left implied.
                        */}
                        {entry.taxRegistrationMatched ? 'tax registration matches' : ''}
                      </span>
                    </td>
                    <td className="col-actions">
                      {canManage && !exchangeBlocked ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setRequesting(entry);
                            setRole('MyVendor');
                            setRequestMessage('');
                            // Arrived from that vendor's own card: carry it straight into the
                            // binding instead of making the operator search for it again here.
                            setLocalVendorId(prefillVendorId ?? '');
                            setLocalVendorName(prefillVendorId ? prefillVendorName : null);
                            setLocalCustomerId('');
                            setError(null);
                          }}
                        >
                          Request connection
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          ) : null}
        </Panel>
      ) : null}

      {/* ---------------------------------------------------------------- request a connection */}
      <Drawer
        open={requesting !== null}
        onClose={() => setRequesting(null)}
        title="Request a connection"
        description={`${requesting?.companyName ?? 'This company'} has to accept it on their side before anything can be exchanged.`}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setRequesting(null)} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => {
                const target = requesting;
                if (!target) return;

                // iERP AI refuses the request outright without this - confirmed live, and Business
                // Central's own ToolKit requires the same pre-existing card before it even shows the
                // request action. Checked here so the failure reads as "pick a vendor first" rather
                // than a server error after the drawer has already closed.
                if (role === 'MyVendor' && !localVendorId) {
                  setError('A QuickBooks vendor is required when they are your vendor — search or create one below.');
                  return;
                }
                if (role === 'MyCustomer' && !localCustomerId) {
                  setError('A QuickBooks customer id is required when they are your customer.');
                  return;
                }

                setRequesting(null);
                void run('Connection requested. It is theirs to accept.', () =>
                  partnerConnections.request({
                    targetProfileId: target.profileId,
                    targetRegistrationId: target.registrationId,
                    targetTenantId: target.tenantId,
                    targetCompanyId: target.companyId,
                    targetCompanyName: target.companyName,
                    targetBusinessEmailDomain: target.businessEmailDomain,
                    relationshipRole: role,
                    message: requestMessage || null,
                    localVendorId: localVendorId || null,
                    localCustomerId: localCustomerId || null,
                  }),
                );
              }}
            >
              {busy ? 'Requesting…' : 'Send the request'}
            </button>
          </>
        }
      >
        <InlineError message={error} />

        <SelectField
          label="What is this company to us?"
          value={role}
          onChange={(v) => setRole(v as RelationshipRole)}
          options={ROLE_OPTIONS}
          hint="This decides which side controls sending and which controls receiving."
        />

        {/*
          iERP AI itself refuses a MyVendor request with no vendor bound - this is not an
          optimisation that can wait for "Manage" later, whatever an earlier version of this hint
          said. Business Central's ToolKit enforces the identical rule before it even offers the
          request action. TradingPartner does not carry the same hard requirement, so the lookup is
          offered there but not demanded.
        */}
        {role !== 'MyCustomer' ? (
          <VendorLookupField
            label={role === 'MyVendor' ? 'QuickBooks Vendor (required)' : 'QuickBooks Vendor (optional)'}
            selectedId={localVendorId || null}
            selectedName={localVendorName}
            onChange={(id, name) => {
              setLocalVendorId(id ?? '');
              setLocalVendorName(name);
            }}
            hint="Their invoices post against this vendor. Search the cached list, or create it here if it doesn't exist yet."
          />
        ) : null}

        {role !== 'MyVendor' ? (
          <TextField
            label={role === 'MyCustomer' ? 'QuickBooks Customer id (required)' : 'QuickBooks Customer id (optional)'}
            value={localCustomerId}
            onChange={setLocalCustomerId}
            hint="Used when this company is the seller on the connection."
          />
        ) : null}

        <TextAreaField
          label="Message to them (optional)"
          value={requestMessage}
          onChange={setRequestMessage}
          rows={3}
          hint="Shown to the administrator who answers the request."
        />
      </Drawer>

      {/* ------------------------------------------------------- answer / manage a connection */}
      <Drawer
        open={managing !== null}
        onClose={() => setManaging(null)}
        title={managing?.status === 'Requested' ? 'Answer this request' : 'Manage this connection'}
        description={managing?.counterpartyCompanyName ?? undefined}
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setManaging(null)} disabled={busy}>
            Close
          </button>
        }
      >
        {managing ? (
          <div className="stack">
            <InlineError message={error} />

            {managing.message ? (
              <Panel title="What they said">
                <p className="muted">{managing.message}</p>
              </Panel>
            ) : null}

            {managing.status === 'Requested' && managing.direction === 'Inbound' ? (
              <Panel
                title="Accept or reject"
                description="Accepting agrees to exchange documents with this company. Naming the QuickBooks record now means an inbound invoice has somewhere to post."
              >
                <VendorLookupField
                  label="QuickBooks Vendor (optional)"
                  selectedId={bindVendorId || null}
                  selectedName={bindVendorName}
                  onChange={(id, name) => {
                    setBindVendorId(id ?? '');
                    setBindVendorName(name);
                  }}
                  hint="Search the cached list, or create it here if it doesn't exist yet. Can also be set afterwards from Manage."
                />
                <Toolbar>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => {
                      const c = managing;
                      setManaging(null);
                      void run('Connection accepted.', () =>
                        partnerConnections.respond(c.connectionId, {
                          accept: true,
                          localVendorId: bindVendorId || null,
                        }),
                      );
                    }}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => {
                      const c = managing;
                      setManaging(null);
                      void run('Connection rejected.', () =>
                        partnerConnections.respond(c.connectionId, { accept: false }),
                      );
                    }}
                  >
                    Reject
                  </button>
                </Toolbar>
              </Panel>
            ) : null}

            {managing.status === 'Accepted' ? (
              <>
                <Panel
                  title="Which QuickBooks record is this company?"
                  description="An inbound invoice from this partner posts against this record. Without it the invoice has no supplier and cannot post."
                >
                  {managing.isBuyer ? (
                    <VendorLookupField
                      label="QuickBooks Vendor"
                      selectedId={bindVendorId || null}
                      selectedName={bindVendorName}
                      onChange={(id, name) => {
                        setBindVendorId(id ?? '');
                        setBindVendorName(name);
                      }}
                      hint="Search the cached list, or create it here if it doesn't exist yet."
                    />
                  ) : (
                    <TextField
                      label="QuickBooks Customer id"
                      value={bindCustomerId}
                      onChange={setBindCustomerId}
                    />
                  )}
                  <Toolbar>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy}
                      onClick={() => {
                        const c = managing;
                        void run('Connection bound.', () =>
                          partnerConnections.bind(c.connectionId, {
                            qboVendorId: c.isBuyer ? bindVendorId || null : null,
                            qboCustomerId: c.isBuyer ? null : bindCustomerId || null,
                          }),
                        );
                      }}
                    >
                      Save the binding
                    </button>
                  </Toolbar>
                  {managing.boundByEmail ? (
                    <p className="drawer-note">
                      Bound by {managing.boundByEmail}
                      {managing.boundUtc ? ` on ${formatDateTime(managing.boundUtc)}` : ''}.
                    </p>
                  ) : null}
                </Panel>

                {managing.isBuyer ? (
                  <Panel
                    title="Vendor by currency"
                    description="QuickBooks locks a vendor's currency permanently, so a partner invoicing in more than one currency needs a second vendor card there. An invoice in a currency listed here posts to that vendor instead of the one bound above."
                  >
                    {Object.keys(managing.currencyVendorLinks).length > 0 ? (
                      <TableWrap>
                        <thead>
                          <tr>
                            <th scope="col">Currency</th>
                            <th scope="col">QuickBooks vendor</th>
                            <th scope="col" />
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(managing.currencyVendorLinks).map(([currency, vendorId]) => (
                            <tr key={currency}>
                              <td>{currency}</td>
                              <td className="mono">{vendorId}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  disabled={busy}
                                  onClick={() => {
                                    const c = managing;
                                    void run(`${currency} vendor routing removed.`, () =>
                                      partnerConnections.unlinkCurrencyVendor(c.connectionId, currency),
                                    );
                                  }}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </TableWrap>
                    ) : (
                      <p className="muted">No currency-specific vendor yet — every invoice posts to the bound vendor above.</p>
                    )}

                    <TextField
                      label="Currency code"
                      value={currencyLinkCode}
                      onChange={(value) => setCurrencyLinkCode(value.toUpperCase())}
                      placeholder="GBP"
                    />
                    <VendorLookupField
                      label="QuickBooks vendor for that currency"
                      selectedId={currencyLinkVendorId || null}
                      selectedName={currencyLinkVendorName}
                      onChange={(id, name) => {
                        setCurrencyLinkVendorId(id ?? '');
                        setCurrencyLinkVendorName(name);
                      }}
                      hint="Search the cached list, or create it here if it doesn't exist yet — it must already carry this currency in QuickBooks."
                    />
                    <Toolbar>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={busy || !currencyLinkCode.trim() || !currencyLinkVendorId}
                        onClick={() => {
                          const c = managing;
                          const currency = currencyLinkCode.trim();
                          const vendorId = currencyLinkVendorId;
                          setCurrencyLinkCode('');
                          setCurrencyLinkVendorId('');
                          setCurrencyLinkVendorName(null);
                          void run(`${currency} routed to this vendor.`, () =>
                            partnerConnections.linkCurrencyVendor(c.connectionId, {
                              currencyCode: currency,
                              qboVendorId: vendorId,
                            }),
                          );
                        }}
                      >
                        Add
                      </button>
                    </Toolbar>
                  </Panel>
                ) : null}

                <Panel
                  title="Exchange"
                  description="Only the seller controls sending and only the buyer controls receiving. The switch below is this company's."
                >
                  <CheckboxField
                    label={managing.isBuyer ? 'Receive invoices from this partner' : 'Send invoices to this partner'}
                    checked={
                      managing.isBuyer
                        ? managing.buyerInvoiceReceivingEnabled
                        : managing.sellerInvoiceSendingEnabled
                    }
                    onChange={(enabled) => {
                      const c = managing;
                      void run(enabled ? 'Exchange resumed.' : 'Exchange paused.', () =>
                        partnerConnections.setCapability(
                          c.connectionId,
                          c.isBuyer ? 'BuyerInvoiceReceiving' : 'SellerInvoiceSending',
                          enabled,
                        ),
                      );
                    }}
                    hint="Pausing holds deliveries at iERP AI rather than losing them; resuming lets them through."
                  />

                  <CheckboxField
                    label="Apply their company-information updates automatically"
                    checked={managing.autoApplyCompanyInfoUpdates}
                    onChange={(enabled) => {
                      const c = managing;
                      void run('Policy saved.', () =>
                        partnerConnections.setAutoApply(c.connectionId, enabled),
                      );
                    }}
                    hint="When off, a change of their address or tax registration waits for someone here to accept it."
                  />

                  {managing.capabilityChangedUtc ? (
                    <p className="drawer-note">
                      Last changed {formatDateTime(managing.capabilityChangedUtc)}.
                    </p>
                  ) : null}
                </Panel>

                {managing.hasPendingCompanyInfoUpdate ? (
                  <Panel
                    title="They changed their company information"
                    description="Received but not applied. Accepting updates what this gateway holds for them."
                  >
                    <p className="muted">
                      Received{' '}
                      {managing.pendingUpdateReceivedUtc
                        ? formatDateTime(managing.pendingUpdateReceivedUtc)
                        : '—'}
                      .
                    </p>
                    <Toolbar>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={busy}
                        onClick={() => {
                          const c = managing;
                          setManaging(null);
                          void run('Their update was applied.', () =>
                            partnerConnections.respondCompanyInfo(c.connectionId, true),
                          );
                        }}
                      >
                        Accept their update
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={busy}
                        onClick={() => {
                          const c = managing;
                          setManaging(null);
                          void run('Their update was rejected.', () =>
                            partnerConnections.respondCompanyInfo(c.connectionId, false),
                          );
                        }}
                      >
                        Reject it
                      </button>
                    </Toolbar>
                  </Panel>
                ) : null}

                <Panel title="Other actions">
                  <Toolbar>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => {
                        const c = managing;
                        void run('This company profile was resent.', () =>
                          partnerConnections.resendCompanyInfo(c.connectionId),
                        );
                      }}
                    >
                      Resend our company info
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() => {
                        setRevoking(managing);
                        setManaging(null);
                      }}
                    >
                      Revoke the connection
                    </button>
                  </Toolbar>
                </Panel>
              </>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      {/*
        Revoking is the one action here that another company sees and cannot undo from their side,
        so it asks for a reason - which travels to them rather than being kept locally.
      */}
      <ConfirmDialog
        open={revoking !== null}
        tone="danger"
        title="Revoke this connection?"
        message={
          `${revoking?.counterpartyCompanyName ?? 'This company'} will stop being able to exchange `
          + 'documents with this company. Existing documents already received are unaffected.'
        }
        confirmLabel="Revoke"
        requireReason
        reasonLabel="Reason (sent to them)"
        pending={busy}
        onCancel={() => setRevoking(null)}
        onConfirm={(reason) => {
          const c = revoking;
          setRevoking(null);
          if (c) {
            void run('Connection revoked.', () =>
              partnerConnections.revoke(c.connectionId, reason || null),
            );
          }
        }}
      />
    </>
  );
}
